import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { sendEvent } from '../../utils/analytics';

import Results from './results';
import OutOfBounds from './results/out-of-bounds';
import NoResults from './results/no-results';
import Error from './results/error';
import StartAgain from './location-selectors/start-again';

import LocationSelector from './location-selectors';

import { Heading, Wrap } from './styles';

/**
 * Looks up a service via an API call. Displays the result, plus
 * some generic answer text for each result.
 *
 * Should handle when the result is marked as out-of-bounds or
 * if there are no results returned
 */
const Lookup = ({ title, additionalAnswerText, serviceType }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const doLookup = (locationType, locationValue) => {
    const doFetch = async () => {
      try {
        const res = await axios.post('/api/local-authority-lookup', {
          locationType,
          locationValue,
          serviceType,
        });

        setData(res.data);

        sendEvent({
          searchStatus: res.data.outOfBounds ? 'Out of Bounds' : 'Success',
          locationEntered:
            locationType === 'gps' ? 'Use my location' : locationValue,
          loccationReturned:
            res.data.outOfBounds || !res.data.props
              ? ''
              : res.data.props.council,
          serviceType,
          event: 'localAuthoritySearch',
        });
      } catch (e) {
        setError(e.response.status);

        sendEvent({
          searchStatus: 'Error',
          locationEntered:
            locationType === 'gps' ? 'Use my location' : locationValue,
          event: 'localAuthoritySearch',
        });
      }
    };

    doFetch();
  };

  return (
    <Wrap>
      <Heading>{title}</Heading>
      {!error && data === null && (
        <LocationSelector
          lookup={({ type, value }) => doLookup(type, value)}
          reset={data !== null ? () => setData(null) : null}
        />
      )}
      {error && (
        <>
          <Error />
          <StartAgain
            cancel={() => {
              setData(null);
              setError(null);
            }}
          />
        </>
      )}
      {data !== null && data.total > 0 && (
        <Results
          serviceType={serviceType}
          additionalAnswerText={additionalAnswerText}
          {...data.props}
        />
      )}
      {data !== null && data.outOfBounds && <OutOfBounds />}
      {data !== null && !data.outOfBounds && data.total === 0 && <NoResults />}
      {data !== null && <StartAgain cancel={() => setData(null)} />}
    </Wrap>
  );
};

Lookup.propTypes = {
  /** Will be displayed to the user if present */
  title: PropTypes.string,
  /** The key for choosing which query to execute and result to display */
  serviceType: PropTypes.string.isRequired,
  /** Generic text that is shown with every answer */
  additionalAnswerText: PropTypes.object,
};

export default Lookup;

export const LookupAssembly = ({ contentfulData }) => (
  <Lookup
    title={contentfulData.title}
    additionalAnswerText={contentfulData.additionalAnswerText}
    serviceType={contentfulData.serviceType}
  />
);

LookupAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
